/**
 * src/components/documento/CustomColorSwatch.tsx
 * =============================================================================
 * Swatch de "color personalizado" del panel de colores de la toolbar.
 *
 * La paleta cerrada de `ANNOTATION_COLORS` cubre el 90% de los casos, pero el
 * dueño pidió (2026-09-03) poder elegir cualquier color. Este swatch es un
 * botón redondo más — mismo tamaño h-7 w-7 — que abre el selector de color
 * NATIVO del navegador (`<input type="color">`, sin librerías):
 *
 *  - Sin color recordado: se pinta un degradado cónico de arcoíris ("aquí hay
 *    más colores").
 *  - El último color elegido se recuerda en localStorage (clave
 *    `ploter:color-personalizado`, envuelta en try/catch para modo privado)
 *    y el swatch pasa a mostrarlo, como un color más de la paleta.
 *  - Si la anotación seleccionada trae un color que NO está en la paleta
 *    (p. ej. la puso otro usuario), el swatch lo refleja como activo.
 *
 * El valor emitido es SIEMPRE `#rrggbb` en minúsculas — el backend valida hex
 * estricto y el input nativo ya garantiza ese formato.
 * =============================================================================
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ANNOTATION_COLORS } from '@/types/annotation'

/** Clave de localStorage — prefijo `ploter:` como el resto del proyecto. */
const STORAGE_KEY = 'ploter:color-personalizado'

/** Formato estricto que exige el backend. El input nativo siempre lo cumple. */
const HEX_RE = /^#[0-9a-f]{6}$/i

/** Lee el último color personalizado guardado. null si no hay o no es válido. */
function leerColorGuardado(): string | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v && HEX_RE.test(v) ? v.toLowerCase() : null
  } catch {
    // Modo privado / storage bloqueado: sin memoria, pero el picker funciona.
    return null
  }
}

/** Persiste el color elegido. Fallar en silencio es aceptable aquí. */
function guardarColor(hex: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, hex)
  } catch {
    /* sin persistencia — no rompemos el flujo de pintar */
  }
}

interface Props {
  /** Color actualmente activo (de la selección o el default para nuevas). */
  activeColor: string
  /** Aplica el color igual que un swatch de la paleta fija. */
  onPick: (hex: string) => void
}

export function CustomColorSwatch({ activeColor, onPick }: Props) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [customColor, setCustomColor] = useState<string | null>(() => leerColorGuardado())

  const label = t('visor.herramientas.colorPersonalizado')
  const activeLower = activeColor.toLowerCase()
  const enPaleta = ANNOTATION_COLORS.some((c) => c.hex.toLowerCase() === activeLower)
  // Activo si el color vigente es "suyo": el recordado, o cualquiera fuera
  // de la paleta (anotación seleccionada con un color libre de otro usuario).
  const isActive = !enPaleta || activeLower === customColor
  // Qué enseña el círculo: el color activo fuera de paleta > el recordado >
  // el arcoíris de "elige uno".
  const shownColor = !enPaleta ? activeLower : customColor
  // Punto de partida del picker nativo (necesita un value #rrggbb válido).
  const pickerValue = shownColor ?? '#dc2626'

  function handlePicked(raw: string) {
    // El input nativo emite #rrggbb en minúsculas; validamos por si acaso.
    if (!HEX_RE.test(raw)) return
    const hex = raw.toLowerCase()
    setCustomColor(hex)
    guardarColor(hex)
    onPick(hex)
  }

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      aria-label={label}
      title={label}
      // `touch-manipulation` evita el retraso de ~300ms del doble-tap-zoom
      // en pantallas táctiles (Surface) — ver nota en AnnotationToolbar.
      className={`relative h-7 w-7 rounded-full border-2 transition active:scale-95 touch-manipulation ${
        isActive ? 'border-zinc-900 scale-110' : 'border-white hover:scale-105'
      }`}
      style={{
        background: shownColor
          ? shownColor
          : // Arcoíris cónico: lenguaje universal de "selector de color libre".
            'conic-gradient(#ef4444, #f59e0b, #eab308, #22c55e, #06b6d4, #3b82f6, #a855f7, #ef4444)',
      }}
    >
      {/* Input nativo invisible. Lo abrimos con click programático — así el
          botón visible controla el estilo y el input aporta solo el picker
          del sistema. `sr-only` lo mantiene en el DOM (algunos navegadores
          ignoran .click() sobre display:none). */}
      <input
        ref={inputRef}
        type="color"
        value={pickerValue}
        onChange={(e) => handlePicked(e.target.value)}
        // El click programático sobre el input burbujea hasta el botón: sin
        // este stopPropagation se re-dispararía el onClick en bucle.
        onClick={(e) => e.stopPropagation()}
        aria-hidden
        tabIndex={-1}
        className="sr-only"
      />
    </button>
  )
}
